import { Autocomplete, Box, Chip, createFilterOptions, Stack, TextField } from "@mui/material";
import type { FC, SyntheticEvent } from "react";
import { useEffect, useState } from "react";
import { useUpsertAssetCallback } from "./hooks/use-upsert-asset";
import type { Asset } from "./server/digital-asset-service";

const filter = createFilterOptions<string>();

export const AssetTagManager: FC<{
  localTags: string[];
  setLocalTags: (tags: string[]) => void;
  asset?: Asset;
  availableTags?: string[];
}> = ({ localTags, setLocalTags, asset, availableTags = [] }) => {
  const upsertAssetCallback = useUpsertAssetCallback();
  const [value, setValue] = useState<string | null>(null);
  const [inputText, setInputText] = useState<string>("");

  useEffect(() => {
    if (!asset) return;
    setLocalTags(asset.tags || []);
  }, [asset, setLocalTags]);

  const handleDelete = async (tag: string): Promise<void> => {
    if (!localTags) return;

    const updatedTags = localTags.filter((t) => t !== tag);

    if (asset) {
      await upsertAssetCallback({ ...asset, tags: updatedTags.length > 0 ? updatedTags : null });
    }

    setLocalTags(updatedTags);
  };

  const handleChange = async (event: SyntheticEvent, newValue: string | null): Promise<void> => {
    if (!newValue) {
      return;
    }

    if (localTags.includes(newValue)) {
      setValue(null);
      setInputText("");
      return;
    }

    const newTags = [...localTags, newValue];

    if (asset) {
      await upsertAssetCallback({ ...asset, tags: newTags });
    }

    setLocalTags(newTags);
    setValue(null);
    setInputText("");
  };

  return (
    <Box>
      <Autocomplete
        value={value}
        inputValue={inputText}
        onChange={(event, newValue) => {
          void handleChange(event, newValue);
        }}
        onInputChange={(event, newInputValue) => {
          setInputText(newInputValue);
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);

          const inputValue = params.inputValue;
          const isExisting = options.some((option) => inputValue === option);
          if (inputValue !== "" && !isExisting) {
            filtered.push(inputValue);
          }

          return filtered;
        }}
        selectOnFocus
        clearOnBlur
        handleHomeEndKeys
        id="tag-manager-input"
        options={availableTags.filter((option) => !localTags.includes(option))}
        getOptionLabel={(option) => {
          return option;
        }}
        renderOption={(props, option) => {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          const { key, ...optionProps } = props;
          return (
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            <li key={key} {...optionProps}>
              <Chip label={option} color="primary" />
            </li>
          );
        }}
        fullWidth
        freeSolo
        renderInput={(params) => <TextField {...params} label="Add tags" />}
      />
      {localTags.length > 0 && (
        <Stack direction="row" mt={2} gap={1} useFlexGap flexWrap="wrap">
          {localTags.map((tag, index) => (
            <Chip key={index} label={tag} color="primary" onDelete={() => void handleDelete(tag)} />
          ))}
        </Stack>
      )}
    </Box>
  );
};
